import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api } from '../services/api'
import { User } from '../types'
import { toast } from 'sonner'

interface UserContextType {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  tokenValidated: boolean
  setUser: (user: User | null) => void
  refreshUser: () => Promise<void>
  logout: () => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

interface UserProviderProps {
  children: ReactNode
}

export function UserProvider({ children }: UserProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [tokenValidated, setTokenValidated] = useState(false)

  // Validate token and fetch user on mount
  useEffect(() => {
    validateToken()
  }, [])

  // Listen for login/logout events to refresh user data
  useEffect(() => {
    const handleUserLoggedIn = () => {
      console.log('User logged in event received, refreshing user data...')
      validateToken()
    }

    const handleUserLoggedOut = () => {
      console.log('User logged out event received, clearing user data...')
      setUser(null)
      setIsAuthenticated(false)
    }

    const handleStorageChange = (e: StorageEvent) => {
      // Listen for auth_token changes from other tabs
      if (e.key === 'auth_token') {
        if (e.newValue) {
          // Token added - validate it
          console.log('Auth token added in another tab, validating...')
          validateToken()
        } else {
          // Token removed - clear user
          console.log('Auth token removed in another tab, clearing user...')
          setUser(null)
          setIsAuthenticated(false)
        }
      }
    }

    window.addEventListener('user-logged-in', handleUserLoggedIn)
    window.addEventListener('user-logged-out', handleUserLoggedOut)
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('user-logged-in', handleUserLoggedIn)
      window.removeEventListener('user-logged-out', handleUserLoggedOut)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const validateToken = async () => {
    const token = localStorage.getItem('auth_token')

    if (!token) {
      setLoading(false)
      setIsAuthenticated(false)
      setTokenValidated(true)
      return
    }

    try {
      // Validate token by calling /auth/me
      console.log('Validating token on mount...')
      const response = await api.getCurrentUser()

      if (response.success && response.data) {
        console.log('Token valid, user:', response.data.username)
        setUser(response.data)
        setIsAuthenticated(true)
      } else {
        // Token exists but invalid response
        console.log('Token validation failed: invalid response')
        handleInvalidToken()
      }
    } catch (error: any) {
      // 401 or other error - token is invalid
      console.log('Token validation failed:', error.response?.status)
      if (error.response?.status === 401) {
        handleInvalidToken()
      } else {
        // Other error - treat as unauthenticated but don't clear token yet
        setIsAuthenticated(false)
      }
    } finally {
      setLoading(false)
      setTokenValidated(true)
    }
  }

  const handleInvalidToken = () => {
    console.log('Handling invalid token...')
    // Clear auth data
    localStorage.removeItem('auth_token')
    localStorage.removeItem('wallet_disconnected')
    setUser(null)
    setIsAuthenticated(false)

    // Check if we're on a referrer link
    const urlParams = new URLSearchParams(window.location.search)
    const hasReferrer = urlParams.has('ref')

    if (hasReferrer) {
      // Coming from referrer link - emit event for register modal
      console.log('Invalid token with referrer - emitting event')
      window.dispatchEvent(new CustomEvent('show-register-modal', { detail: { ref: urlParams.get('ref') } }))
    } else {
      // Normal case - redirect to sign in if on protected route
      const isProtectedRoute = window.location.pathname.includes('/dashboard') ||
                              window.location.pathname.includes('/portfolio') ||
                              window.location.pathname.includes('/profile')

      if (isProtectedRoute) {
        console.log('Invalid token on protected route - redirecting to sign in')
        toast.error('Session expired. Please sign in again.')
        setTimeout(() => {
          window.location.href = '/app?signin=true'
        }, 1000)
      }
    }
  }

  const refreshUser = async (): Promise<void> => {
    try {
      const response = await api.getCurrentUser()
      if (response.success && response.data) {
        setUser(response.data)
        setIsAuthenticated(true)
      }
    } catch (error) {
      console.error('Error refreshing user:', error)
      if ((error as any).response?.status === 401) {
        handleInvalidToken()
      }
    }
  }

  const logout = () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('wallet_disconnected')
    setUser(null)
    setIsAuthenticated(false)
    window.location.href = '/app'
  }

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        tokenValidated,
        setUser,
        refreshUser,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
